import React, { useState, useEffect } from 'react';
import { Star, Heart, Zap, Shield, Sparkles } from 'lucide-react';

const zodiacSigns = [
  { name: 'Aries', symbol: '\u2648', dates: 'Mar 21 - Apr 19', element: 'Fire', ruler: 'Mars',
    crystal: 'Carnelian', color: '#FF6B6B', traits: ['Bold', 'Ambitious', 'Energetic'],
    crystalNote: 'Ignites your warrior courage and creative drive' },
  { name: 'Taurus', symbol: '\u2649', dates: 'Apr 20 - May 20', element: 'Earth', ruler: 'Venus',
    crystal: 'Rose Quartz', color: '#00C853', traits: ['Reliable', 'Patient', 'Devoted'],
    crystalNote: 'Softens your heart while grounding your sensual nature' },
  { name: 'Gemini', symbol: '\u264A', dates: 'May 21 - Jun 20', element: 'Air', ruler: 'Mercury',
    crystal: 'Citrine', color: '#FFB800', traits: ['Adaptable', 'Curious', 'Expressive'],
    crystalNote: 'Sharpens your mental agility and communication' },
  { name: 'Cancer', symbol: '\u264B', dates: 'Jun 21 - Jul 22', element: 'Water', ruler: 'Moon',
    crystal: 'Moonstone', color: '#C0C0C0', traits: ['Intuitive', 'Nurturing', 'Protective'],
    crystalNote: 'Deepens your lunar connection and emotional wisdom' },
  { name: 'Leo', symbol: '\u264C', dates: 'Jul 23 - Aug 22', element: 'Fire', ruler: 'Sun',
    crystal: "Tiger's Eye", color: '#FF9F43', traits: ['Confident', 'Generous', 'Creative'],
    crystalNote: 'Amplifies your radiant leadership and solar power' },
  { name: 'Virgo', symbol: '\u264D', dates: 'Aug 23 - Sep 22', element: 'Earth', ruler: 'Mercury',
    crystal: 'Amazonite', color: '#00B4D8', traits: ['Analytical', 'Kind', 'Hardworking'],
    crystalNote: 'Balances your perfectionist nature with soothing calm' },
  { name: 'Libra', symbol: '\u264E', dates: 'Sep 23 - Oct 22', element: 'Air', ruler: 'Venus',
    crystal: 'Lapis Lazuli', color: '#3B82F6', traits: ['Harmonious', 'Fair', 'Social'],
    crystalNote: 'Activates your inner truth and aesthetic vision' },
  { name: 'Scorpio', symbol: '\u264F', dates: 'Oct 23 - Nov 21', element: 'Water', ruler: 'Pluto',
    crystal: 'Black Obsidian', color: '#4A4A6A', traits: ['Passionate', 'Resourceful', 'Brave'],
    crystalNote: 'Mirrors your shadow self for deep transformation' },
  { name: 'Sagittarius', symbol: '\u2650', dates: 'Nov 22 - Dec 21', element: 'Fire', ruler: 'Jupiter',
    crystal: 'Turquoise', color: '#40E0D0', traits: ['Optimistic', 'Adventurous', 'Honest'],
    crystalNote: 'Protects your journey and expands spiritual horizons' },
  { name: 'Capricorn', symbol: '\u2651', dates: 'Dec 22 - Jan 19', element: 'Earth', ruler: 'Saturn',
    crystal: 'Garnet', color: '#8B0000', traits: ['Disciplined', 'Responsible', 'Patient'],
    crystalNote: 'Fuels your relentless ambition with vital energy' },
  { name: 'Aquarius', symbol: '\u2652', dates: 'Jan 20 - Feb 18', element: 'Air', ruler: 'Uranus',
    crystal: 'Amethyst', color: '#9D4EDD', traits: ['Independent', 'Humanitarian', 'Original'],
    crystalNote: 'Opens your crown to cosmic downloads and innovation' },
  { name: 'Pisces', symbol: '\u2653', dates: 'Feb 19 - Mar 20', element: 'Water', ruler: 'Neptune',
    crystal: 'Aquamarine', color: '#7FDBFF', traits: ['Compassionate', 'Intuitive', 'Artistic'],
    crystalNote: 'Channels your oceanic empathy into creative flow' }
];

const dailyGuidance = {
  love: [
    "Venus blesses your connections today. Open your heart to sincere gestures and speak your needs aloud.",
    "A deep emotional current flows between you and a significant person. Honor the vulnerability — it's where intimacy lives.",
    "The cosmic energy invites self-love first. Treat yourself as you wish to be treated by another.",
    "Someone may reveal hidden feelings. Receive gracefully; your compassion today builds lasting bonds.",
    "Romance thrives when you release expectations. Let love surprise you with its timing."
  ],
  career: [
    "Mars energizes your professional sector. Channel this fire into one decisive action — not scattered ambition.",
    "A mentor figure appears through an unlikely channel. Stay receptive to wisdom from unexpected voices.",
    "Financial clarity arrives mid-day. Review your resources and invest in what aligns with your true purpose.",
    "Collaboration amplifies your success tenfold. Partner with someone whose strengths complement yours.",
    "The seeds you planted weeks ago are sprouting. Trust the growth even when you can't see the roots."
  ],
  wellness: [
    "Your root chakra craves attention. Walk barefoot on earth, hold a grounding stone, and breathe deeply.",
    "Emotional residue is ready for release. Pair a cleansing selenite session with a warm bath to reset your energy field.",
    "Your body is speaking — listen to it. The discomfort is not punishment; it's guidance toward alignment.",
    "Crystal meditation is especially powerful for you today. Hold your birthstone and set a 10-minute intention.",
    "Hydrate mindfully. Water carries intention — bless each glass with gratitude before drinking."
  ]
};

const getZodiacFromDate = (month, day) => {
  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return zodiacSigns[0];
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return zodiacSigns[1];
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return zodiacSigns[2];
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return zodiacSigns[3];
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return zodiacSigns[4];
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return zodiacSigns[5];
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return zodiacSigns[6];
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return zodiacSigns[7];
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return zodiacSigns[8];
  if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return zodiacSigns[9];
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return zodiacSigns[10];
  return zodiacSigns[11];
};

const AstrologyWidget = () => {
  const [selectedSign, setSelectedSign] = useState(null);
  const [dailyReading, setDailyReading] = useState(null);
  const [luckyNumbers, setLuckyNumbers] = useState([]);

  useEffect(() => {
    const today = new Date();
    const currentSign = getZodiacFromDate(today.getMonth() + 1, today.getDate());
    setSelectedSign(currentSign);
    generateReading(currentSign);
  }, []);

  const generateReading = (sign) => {
    const today = new Date();
    const seed = today.getFullYear() * 366 + today.getMonth() * 31 + today.getDate();
    const signOffset = sign.name.charCodeAt(0) + sign.name.charCodeAt(sign.name.length - 1);

    const reading = {
      love: dailyGuidance.love[(seed + signOffset) % dailyGuidance.love.length],
      career: dailyGuidance.career[(seed + signOffset + 2) % dailyGuidance.career.length],
      wellness: dailyGuidance.wellness[(seed + signOffset + 4) % dailyGuidance.wellness.length],
    };
    setDailyReading(reading);

    const nums = new Set();
    let i = 0;
    while (nums.size < 3) {
      nums.add(((seed * (i + 3) + signOffset * (i + 1)) % 49) + 1);
      i++;
    }
    setLuckyNumbers([...nums]);
  };

  const handleSignSelect = (sign) => {
    setSelectedSign(sign);
    generateReading(sign);
  };

  if (!selectedSign || !dailyReading) return null;

  return (
    <div className="bg-gradient-to-br from-[#1e1919] via-[#2a2030] to-[#1e1919] rounded-2xl overflow-hidden" data-testid="astrology-widget">
      <div className="bg-gradient-to-r from-[#9D4EDD]/20 to-[#FF6B9D]/20 p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-[#FFB800] fill-current" />
          <h3 className="font-serif text-lg text-white">Daily Cosmic Guidance</h3>
        </div>
      </div>

      <div className="p-4 border-b border-white/10">
        <p className="text-xs uppercase tracking-wider text-[#c9a96e] mb-3">Select Your Sign</p>
        <div className="grid grid-cols-6 gap-2">
          {zodiacSigns.map((sign) => (
            <button
              key={sign.name}
              onClick={() => handleSignSelect(sign)}
              className={`p-2 rounded-lg text-center transition-all ${
                selectedSign?.name === sign.name
                  ? 'bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] text-white scale-110 shadow-lg shadow-[#9D4EDD]/30'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
              title={`${sign.name} (${sign.dates})`}
              data-testid={`zodiac-${sign.name.toLowerCase()}`}
            >
              <span className="text-xl">{sign.symbol}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center gap-4 mb-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
            style={{ backgroundColor: `${selectedSign.color}20`, border: `2px solid ${selectedSign.color}` }}
          >
            {selectedSign.symbol}
          </div>
          <div>
            <h4 className="font-serif text-2xl text-white">{selectedSign.name}</h4>
            <p className="text-sm text-white/60">{selectedSign.dates}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/80">
                {selectedSign.element} / {selectedSign.ruler}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {selectedSign.traits.map((trait, idx) => (
            <span key={idx} className="text-xs px-3 py-1 rounded-full bg-[#9D4EDD]/20 text-[#9D4EDD]">{trait}</span>
          ))}
        </div>

        <div className="space-y-3">
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-2 text-[#FF6B9D] mb-1">
              <Heart className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase">Love & Relationships</span>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">{dailyReading.love}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-2 text-[#FFB800] mb-1">
              <Zap className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase">Career & Finance</span>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">{dailyReading.career}</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <div className="flex items-center gap-2 text-[#00C853] mb-1">
              <Shield className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase">Wellness & Spirit</span>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">{dailyReading.wellness}</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-3">
          <div className="text-center">
            <p className="text-xs text-white/40 mb-1">Lucky Numbers</p>
            <p className="text-lg font-bold text-[#FFB800]">{luckyNumbers.join(', ')}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-white/40 mb-1">Power Crystal</p>
            <p className="text-sm font-semibold text-[#9D4EDD]">{selectedSign.crystal}</p>
          </div>
        </div>

        <div className="mt-4 p-3 bg-gradient-to-r from-[#9D4EDD]/20 to-[#FF6B9D]/20 rounded-xl">
          <p className="text-xs text-white/60 mb-1">
            <Sparkles className="w-3 h-3 inline mr-1 text-[#c9a96e]" />
            Why <span className="text-[#c9a96e] font-semibold">{selectedSign.crystal}</span> for {selectedSign.name}:
          </p>
          <p className="text-sm text-white/80 italic">{selectedSign.crystalNote}</p>
        </div>
      </div>
    </div>
  );
};

export default AstrologyWidget;
